"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";
import { formatDistanceToNow } from "date-fns";

export default function NotificationCenter() {
  const [notifications, setNotifications] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('unread'); // 'unread' or 'past'
  const dropdownRef = useRef(null);
  const router = useRouter();

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch notifications (which now include announcements)
      const notificationsRes = await fetch("/api/notifications", { cache: "no-store" }).catch(() => ({ ok: false }));

      let notificationsData = [];
      let unreadCount = 0;

      if (notificationsRes.ok) {
        try {
          const data = await notificationsRes.json();
          notificationsData = data.notifications || [];
          unreadCount = data.unreadCount || 0;
        } catch (e) {
          console.error("Error parsing notifications:", e);
        }
      }

      setNotifications(notificationsData);
      setAnnouncements([]); // Announcements are now in notifications
      setUnreadCount(unreadCount);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    // Poll for new notifications every hour to reduce database queries
    const interval = setInterval(fetchAll, 3600000); // 1 hour = 3600000ms
    return () => clearInterval(interval);
  }, [fetchAll]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Refresh notifications when opening the notification center
      fetchAll();
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, fetchAll]);

  const markAsRead = async (notification) => {
    try {
      const res = await fetch("/api/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notificationId: notification.id }),
      });
      if (res.ok) {
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, isRead: true } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
        
        // If it's an announcement notification, also mark the announcement as read
        if (notification.relatedEntityType === 'announcement' && notification.relatedEntityId) {
          try {
            await fetch("/api/announcements/user", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ announcementId: notification.relatedEntityId }),
            });
          } catch (e) {
            // Silently fail - notification is already marked as read
          }
        }
      }
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      // Mark all notifications as read
      const notificationsRes = await fetch("/api/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ markAllAsRead: true }),
      });

      if (notificationsRes.ok) {
        // Mark all announcement notifications as read in the announcement_reads table
        const announcementNotifications = notifications.filter(
          n => n.relatedEntityType === 'announcement' && n.relatedEntityId && !n.isRead
        );
        
        if (announcementNotifications.length > 0) {
          const announcementIds = [...new Set(announcementNotifications.map(n => n.relatedEntityId))];
          await Promise.all(
            announcementIds.map(announcementId =>
              fetch("/api/announcements/user", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ announcementId }),
              }).catch(() => {}) // Silently fail if it doesn't work
            )
          );
        }

        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification);
    }
    // Show modal instead of redirecting
    setSelectedNotification(notification);
    setIsOpen(false); // Close the dropdown
  };

  const handleCloseModal = () => {
    setSelectedNotification(null);
  };

  const getNotificationIcon = (type) => {
    // Check if it's an announcement notification
    if (type === 'announcement') {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      );
    }
    
    switch (type) {
      case "booking":
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
      case "class":
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case "user":
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-cream hover:text-[#D4AF37] transition-colors touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label="Notifications"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 block h-5 w-5 rounded-full bg-[#D4AF37] text-black text-xs font-bold flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 md:w-96 bg-[#FFF8E7] rounded-lg shadow-xl border border-[#D4AF37] z-50 max-h-[600px] flex flex-col">
          <div className="p-4 border-b border-[#D4AF37]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-black">Notifications</h3>
              {unreadCount > 0 && activeTab === 'unread' && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-[#D4AF37] hover:text-[#B8860B] font-medium touch-manipulation"
                >
                  Mark all as read
                </button>
              )}
            </div>
            
            {/* Tabs */}
            <div className="flex gap-2 border-b border-[#D4AF37] -mb-4">
              <button
                onClick={() => setActiveTab('unread')}
                className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                  activeTab === 'unread'
                    ? 'text-[#D4AF37]'
                    : 'text-gray-600 hover:text-black'
                }`}
              >
                Unread
                {unreadCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-[#FFF8E7] text-[#D4AF37] border border-[#D4AF37] rounded-full text-xs font-semibold">
                    {unreadCount}
                  </span>
                )}
                {activeTab === 'unread' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]"></span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('past')}
                className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                  activeTab === 'past'
                    ? 'text-[#D4AF37]'
                    : 'text-gray-600 hover:text-black'
                }`}
              >
                Past
                {activeTab === 'past' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]"></span>
                )}
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {(() => {
              const filteredNotifications = activeTab === 'unread'
                ? notifications.filter(n => !n.isRead)
                : notifications.filter(n => n.isRead);

              if (filteredNotifications.length === 0) {
                return (
                  <div className="p-8 text-center text-gray-500">
                    <svg
                      className="w-12 h-12 mx-auto mb-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    </svg>
                    <p>
                      {activeTab === 'unread' 
                        ? 'No unread notifications' 
                        : 'No past notifications'}
                    </p>
                  </div>
                );
              }

              return (
                <div className="divide-y divide-gray-200">
                  {filteredNotifications.map((notification) => (
                    <button
                      key={`notification-${notification.id}`}
                      onClick={() => handleNotificationClick(notification)}
                    className={`w-full text-left p-4 hover:bg-[#F5F5DC] transition-colors ${
                      !notification.isRead ? "bg-[#FFF8E7]" : ""
                    }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                        className={`flex-shrink-0 mt-1 ${
                          !notification.isRead
                            ? "text-[#D4AF37]"
                            : "text-gray-500"
                        }`}
                        >
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p
                              className={`text-sm font-medium ${
                                !notification.isRead
                                  ? "text-gray-900"
                                  : "text-gray-700"
                              }`}
                            >
                              {notification.title}
                            </p>
                            {!notification.isRead && (
                              <span className="flex-shrink-0 w-2 h-2 bg-[#D4AF37] rounded-full mt-1.5"></span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-500 mt-2">
                            {notification.createdAt && formatDistanceToNow(new Date(notification.createdAt), {
                              addSuffix: true,
                            })}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {selectedNotification && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={handleCloseModal}
        >
          <div
            className="bg-[#FFF8E7] rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#FFF8E7] border-b border-[#D4AF37] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={`flex-shrink-0 ${
                    !selectedNotification.isRead
                      ? "text-[#D4AF37]"
                      : "text-gray-500"
                  }`}
                >
                  {getNotificationIcon(selectedNotification.type)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-black">
                    {selectedNotification.title}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedNotification.createdAt && formatDistanceToNow(new Date(selectedNotification.createdAt), {
                      addSuffix: true,
                    })}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-black transition-colors p-1 rounded-full hover:bg-[#F5F5DC]"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="prose max-w-none">
                <p className="text-black text-base leading-relaxed whitespace-pre-wrap">
                  {selectedNotification.message}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

